import React from 'react';
import styled from '@emotion/styled';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import { FilterPanel } from './FilterPanel';
import type { FilterPanelControls } from './FilterPanel';
import { FILTER_MOBILE_MAX_WIDTH_PX, filterTokens } from './filterTokens';

const { color } = filterTokens;

/**
 * 조건 저장 — 조건 창(전체 필터·지역·역·직무 등 모든 창) 제목 줄 오른쪽의 [저장하기] 버튼과, 누르면 뜨는 저장 창(2026-10-01 사용자 지시, 2026-10-02 모든 창으로).
 * 어느 창에서 누르든 그 창에서 고르던 것 + 이미 걸려 있는 나머지 조건 전체를 저장한다.
 * 저장한 회원은 목록에 들어올 때마다 그 조건이 켜진 채로 보이고, 알림을 고르면 조건에 맞는 새 글이 올라올 때 알림을 받는다.
 * 지금 고른 조건이 저장한 조건과 같으면 [저장됨] — 다시 누르면 저장을 해제한다. 조건을 바꾸면 [저장하기] 로 돌아가고, 누르면 덮어쓴다.
 */

const SaveButton = styled.button`
    appearance: none;
    flex: 0 0 auto;
    align-self: center;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 32px;
    /* 제목 줄 높이를 키우지 않게 위아래로 당긴다 */
    margin: -4px 0;
    padding: 0 12px 0 8px;
    border: 1px solid ${color.border};
    border-radius: 16px;
    background: ${color.surface};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: ${color.textSub};
    white-space: nowrap;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    & > svg {
        display: block;
        font-size: 18px;
    }
    &[aria-pressed='true'] {
        border-color: ${color.accent};
        background: ${color.selectedBg};
        color: ${color.selectedText};
        font-weight: 700;
    }
    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
    &:focus-visible {
        outline: 2px solid ${color.accent};
        outline-offset: 2px;
    }
`;

export interface FilterSaveButtonProps {
    /** 지금 고른 조건이 저장한 조건과 같은지 */
    saved: boolean;
    /** 저장·해제 요청 중 */
    busy?: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/** 필터 창 제목 줄 오른쪽 — [저장하기] / [저장됨] */
export const FilterSaveButton = ({ saved, busy = false, onClick }: FilterSaveButtonProps) => (
    <SaveButton
        type="button"
        aria-pressed={saved}
        aria-label={saved ? '저장됨 — 누르면 저장을 해제해요' : '이 조건 저장하기'}
        disabled={busy}
        onClick={onClick}
    >
        {saved ? <BookmarkRoundedIcon aria-hidden="true" /> : <BookmarkBorderRoundedIcon aria-hidden="true" />}
        {saved ? '저장됨' : '저장하기'}
    </SaveButton>
);

/** 저장되는 조건 요약 — 지역·역이 들어갔는지 바로 보이게(2026-10-02) */
const SummaryTags = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
`;

const SummaryTag = styled.li`
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-height: 28px;
    padding: 0 10px;
    border-radius: 14px;
    background: ${color.selectedBg};
    color: ${color.selectedText};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    word-break: keep-all;
`;

const Lead = styled.p`
    margin: 12px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${color.textSub};
    word-break: keep-all;
`;

/** 알림 안내 — 작은 회색 글자(사용자 지시) */
const Ask = styled.p`
    margin: 6px 0 4px;
    font-size: 12px;
    line-height: 1.5;
    color: ${color.textMuted};
    /* 좁은 폰에서 두 줄이 될 때 어절 중간에서 끊기거나 마지막 줄에 한두 글자만 남지 않게 */
    word-break: keep-all;
    text-wrap: balance;
`;

const ChoiceButton = styled.button<{ primary?: boolean }>`
    appearance: none;
    flex: 1;
    min-width: 0;
    height: 52px;
    border-radius: 14px;
    /* 두 버튼 폭이 같게 — 주 버튼도 같은 두께의 테두리를 둔다 */
    border: 1px solid ${({ primary }) => (primary ? color.accent : color.border)};
    background: ${({ primary }) => (primary ? color.accent : color.surface)};
    color: ${({ primary }) => (primary ? '#fff' : color.textSub)};
    font-family: inherit;
    font-size: 16px;
    font-weight: 800;
    white-space: nowrap;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
    /* PC 팝업은 창 안 버튼 크기(44px)에 맞춘다 */
    @media (min-width: ${FILTER_MOBILE_MAX_WIDTH_PX + 1}px) {
        height: 44px;
        font-size: 15px;
    }
`;

export interface SaveFilterPanelProps {
    /** PC 팝업을 붙일 [저장하기] 버튼 */
    anchorEl: HTMLElement | null;
    /** 저장되는 조건 요약(탭·지역·역·고른 조건) — 제목 아래에 작은 칩으로 늘어놓는다 */
    summary: string[];
    /**
     * 알림 안내에 넣을 대상(예: "채용공고", "명치사 커리어 세미나").
     * 없으면 알림 안내 없이 [저장] 하나만 둔다(알림이 없는 조건 — 세미나 일반 탭).
     */
    alertTarget?: string;
    saving: boolean;
    onClose: () => void;
    /**
     * notify: 조건에 맞는 새 글 알림을 받을지.
     * closePanel: 이 저장 창을 제대로 닫는 길(폰 시트는 쌓아 둔 방문 기록 칸을 되돌린 뒤 [onClose] 를 부른다) — 저장이 끝나면 부른다
     */
    onSave: (notify: boolean, closePanel: () => void) => void;
}

/** 저장 창 — 폰은 조건 시트 위 시트, PC 는 [저장하기] 버튼 아래 팝업. 저장이 끝나면 이 창을 닫고, 이어서 조건 창이 그 조건을 목록에 적용한다 */
export const SaveFilterPanel = ({ anchorEl, summary, alertTarget, saving, onClose, onSave }: SaveFilterPanelProps) => (
    <FilterPanel
        open
        anchorEl={anchorEl}
        title="이 조건을 저장할게요"
        popoverWidth={340}
        onClose={onClose}
        renderFooter={(controls: FilterPanelControls) => (alertTarget ? (
            <>
                <ChoiceButton type="button" disabled={saving} onClick={() => onSave(false, controls.close)}>저장만</ChoiceButton>
                <ChoiceButton type="button" primary disabled={saving} onClick={() => onSave(true, controls.close)}>알림 받기</ChoiceButton>
            </>
        ) : (
            <ChoiceButton type="button" primary disabled={saving} onClick={() => onSave(false, controls.close)}>저장</ChoiceButton>
        ))}
    >
        {summary.length > 0 && (
            <SummaryTags aria-label="저장되는 조건">
                {summary.map((item: string) => <SummaryTag key={item}>{item}</SummaryTag>)}
            </SummaryTags>
        )}
        <Lead>다음에 들어오면 이 조건이 켜진 채로 보여요.</Lead>
        {alertTarget && <Ask>조건에 맞는 {alertTarget}가 올라오면 알림을 드립니다.</Ask>}
    </FilterPanel>
);
